import { QuestionType, QuestionWithPoints } from '@core/types';
import { Button, Close, Input, Plus, SortArrow } from '@core/ui';
import { t } from 'i18next';

interface Props {
    question: QuestionWithPoints;
    updateQuestion: (question: QuestionWithPoints) => void;
}

export const EditQuestion = ({ question, updateQuestion }: Props) => {
    const moveOptionUp = (index: number) => {
        if (index > 0) {
            const options = [...question.options];
            [options[index - 1], options[index]] = [options[index], options[index - 1]];
            updateQuestion({ ...question, options });
        }
    };

    const moveOptionDown = (index: number) => {
        if (index < question.options.length - 1) {
            const options = [...question.options];
            [options[index], options[index + 1]] = [options[index + 1], options[index]];
            updateQuestion({ ...question, options });
        }
    };

    const getMaxValue = () => {
        return question.options.reduce((max, option) => Math.max(max, option.value || 0), 0);
    };

    return (
        <>
            <h3 className="font-medium mb-6">
                {question.questionType === QuestionType.Open && t('EditQuestionnaire.Heading.OpenQuestion')}
                {question.questionType === QuestionType.SingleChoice && t('EditQuestionnaire.Heading.SingleChoiceQuestion')}
                {question.questionType === QuestionType.MultipleChoice && t('EditQuestionnaire.Heading.MultipleChoiceQuestion')}
            </h3>
            <div className="mb-10">
                <Input
                    autoFocus
                    data-testid="question-input"
                    label={t('EditQuestionnaire.Label.Question')}
                    classNames="mb-6"
                    value={question.questionText}
                    onChange={(e) => updateQuestion({ ...question, questionText: e.target.value })}
                />
                <div className="pl-10">
                    <input
                        id={`question.required.${question.questionId}`}
                        type="checkbox"
                        role="switch"
                        checked={question.required}
                        onChange={(e) => updateQuestion({ ...question, required: e.target.checked })}
                        className="w-2em h-1em appearance-none bg-inherit bg-switch bg-[center_left] focus-visible:not-checked:bg-switch-focus checked:bg-primary checked:bg-switch-checked checked:bg-[center_right] bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-left mt-0.25em -ml-10 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)] motion-safe:transition-[background-position] duration-150 ease-in-out"
                    />
                    <label htmlFor={`question.required.${question.questionId}`} className="inline-block">
                        {t('EditQuestionnaire.Label.RequiredQuestion')}
                    </label>
                </div>
            </div>
            {question.questionType !== QuestionType.Open && (
                <>
                    <h4 className="text-sm font-medium mb-2">{t('EditQuestionnaire.Heading.Options')}</h4>
                    {question.options.map((option, index) => (
                        <div key={`option-${index}`} className="flex flex-col xs:flex-row">
                            <div className="flex-1 flex xs:order-2 -mr-2.5">
                                <Input
                                    autoFocus
                                    data-testid={`question-option-input-${index}`}
                                    label={`${t('EditQuestionnaire.Label.Option', { number: index + 1 })} `}
                                    classNames="max-sm:mb-2"
                                    value={option.text}
                                    onChange={(e) => {
                                        const options = [...question.options];
                                        options[index].text = e.target.value;
                                        updateQuestion({ ...question, options });
                                    }}
                                />
                                <Input
                                    label={t('EditQuestionnaire.Label.OptionValue')}
                                    classNames="flex-none basis-12 ml-4 max-sm:mb-2"
                                    type="number"
                                    value={option.value}
                                    onChange={(e) => {
                                        const options = [...question.options];
                                        options[index].value = parseInt(e.target.value);
                                        updateQuestion({ ...question, options });
                                    }}
                                />
                                <Button
                                    onClick={() => updateQuestion({ ...question, options: question.options.filter((_, i) => i !== index) })}
                                    iconbtn
                                    classNames="my-auto ml-2"
                                >
                                    <Close classNames="size-6 stroke-warning transition-colors ease-in-out duration-200" />
                                    <span className="sr-only">{t('EditQuestionnaire.Button.RemoveOption')}</span>
                                </Button>
                            </div>
                            <div className="flex xs:flex-col xs:order-1 xs:mr-2 xs:-ml-2.5 max-sm:mb-4">
                                <Button onClick={() => moveOptionUp(index)} iconbtn>
                                    <SortArrow classNames="h-5 w-5 rotate-180 stroke-black" />
                                    <span className="sr-only">{t('EditQuestionnaire.Button.MoveUp')}</span>
                                </Button>
                                <Button onClick={() => moveOptionDown(index)} iconbtn>
                                    <SortArrow classNames="h-5 w-5 stroke-black" />
                                    <span className="sr-only">{t('EditQuestionnaire.Button.MoveDown')}</span>
                                </Button>
                            </div>
                        </div>
                    ))}
                    <div className="mb-10">
                        <Button
                            data-testid="question-add-option-button"
                            onClick={() =>
                                updateQuestion({
                                    ...question,
                                    options: [...question.options, { optionId: crypto.randomUUID(), value: getMaxValue() + 1, text: '' }]
                                })
                            }
                            iconbtn
                            variant="primary"
                            classNames="rounded-full!"
                        >
                            <Plus classNames="size-5 stroke-current" />
                            <span className="sr-only">{t('EditQuestionnaire.Button.AddOption')}</span>
                        </Button>
                    </div>
                    <div>
                        <div className="mb-4 pl-10">
                            <input
                                id={`question.hasotheroption.${question.questionId}`}
                                type="checkbox"
                                role="switch"
                                checked={question.hasOtherOption}
                                onChange={(e) => updateQuestion({ ...question, hasOtherOption: e.target.checked })}
                                className="w-2em h-1em appearance-none bg-inherit bg-switch bg-[center_left] focus-visible:not-checked:bg-switch-focus checked:bg-primary checked:bg-switch-checked checked:bg-[center_right] bg-no-repeat border-2 border-gray-500 focus-visible:border-primary checked:border-primary rounded-full float-left mt-0.25em -ml-10 outline-hidden focus-visible:shadow-[0_0_0_.25rem_hsl(var(--c-primary)/.3)] motion-safe:transition-[background-position] duration-150 ease-in-out"
                            />
                            <label htmlFor={`question.hasotheroption.${question.questionId}`} className="inline-block">
                                {t('EditQuestionnaire.Label.HasOtherOption')}
                            </label>
                        </div>
                        {question.hasOtherOption && (
                            <Input
                                label={t('EditQuestionnaire.Label.OtherOptionText')}
                                classNames="mb-6"
                                value={question.otherOptionText}
                                onChange={(e) => updateQuestion({ ...question, otherOptionText: e.target.value })}
                            />
                        )}
                    </div>
                </>
            )}
        </>
    );
};
